import { inject, Injectable } from '@angular/core';
import { groupBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Currency, GqlService, listMonthlyExchangeRatesQuery } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { Utils, ValuesType } from '@shared/utils/utils';

export type ListMonthlyExchangeRates = Omit<listMonthlyExchangeRatesQuery, '__typename'>;

type Exchange = ValuesType<ListMonthlyExchangeRates['exchange_rates']>;

const DEFAULT_EXCHANGE_RATES: ListMonthlyExchangeRates = {
  currencies: [] as listMonthlyExchangeRatesQuery['currencies'],
  exchange_rates: [] as listMonthlyExchangeRatesQuery['exchange_rates'],
  months: [],
  trial_currency: Currency.USD,
  update_date: '',
};

@Injectable({
  providedIn: 'root',
})
export class ExchangeRatesService {
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);

  isLoading$ = new BehaviorSubject(false);
  monthlyExchangeRates$ = new BehaviorSubject<ListMonthlyExchangeRates>(DEFAULT_EXCHANGE_RATES);

  getCurrenciesGridData$() {
    return this.getCurrenciesList$().pipe(
      map(() => {
        const monthlyRates = this.monthlyExchangeRates$.getValue();

        return {
          columns: monthlyRates.currencies.sort(Utils.alphaNumSort),
          rows: this.getGridRowsData(monthlyRates),
        };
      })
    );
  }

  getCurrenciesList$() {
    this.isLoading$.next(true);

    return this.gqlService.listMonthlyExchangeRates$([]).pipe(
      tap(({ data, errors, success }) => {
        if (success && data) {
          this.monthlyExchangeRates$.next(data);
        } else {
          this.overlayService.error(errors);
        }

        this.isLoading$.next(false);
      })
    );
  }

  private getGridRowsData(monthlyRates: ListMonthlyExchangeRates) {
    const groupedExchanges = groupBy(monthlyRates.exchange_rates, 'month') || {};

    return Object.entries<Exchange[]>(groupedExchanges).map(([month, exchanges]) => {
      return {
        date: month,
        ...exchanges.reduce<Record<string, number>>(
          (accum, exchange) => ({
            ...accum,
            [exchange.base_currency]: exchange.rate,
          }),
          {}
        ),
      };
    });
  }
}
